// ProductVolumesChart.js
// Chart showing sales volume (quantity) over time broken down by product and unit

// Imports
import React from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Tooltip, Button, Position, Checkbox } from '@blueprintjs/core';
import { MultiSelect } from '@blueprintjs/select';
import TimeSeriesChart, { aggregateByDay, processData } from './TimeSeriesChart';
import { ValueType, ChartMode, CHART_COLORS } from './chartEnums';

export function getProductUnitArchivedQuantity(order, products, productId, unitId) {
  let total = 0;
  for (const clientId in order.byClient) {
    const productOrder = order.byClient[clientId][productId];
    const prod = products.byId[productId];
    if (prod && productOrder && productOrder.quantity !== undefined) {
      const uId = productOrder.unitId >= prod.price.length ? prod.price.length - 1 : (productOrder.unitId || 0);
      if (uId === unitId) {
        total += productOrder.quantity;
      }
    }
  }
  return total;
}

export function getProductUnitOpenQuantity(currentOrder, products, productId, unitId) {
  if (!currentOrder || !products || !products.byId || !products.byId[productId]) return 0;
  let total = 0;
  const prod = products.byId[productId];

  for (const clientId in currentOrder.byClient) {
    const productOrder = currentOrder.byClient[clientId][productId];
    if (productOrder) {
      const uId = productOrder.unitId >= prod.price.length ? prod.price.length - 1 : (productOrder.unitId || 0);
      if (uId === unitId) {
        total += productOrder.quantity;
      }
    }
  }
  return total;
}

export function convertOrdersToProductUnitDataPoints(orders, products, productId, unitId, includeOpen = true) {
  const dataPoints = [];

  orders.archive.forEach(order => {
    if (order.dateClosed) {
      const total = getProductUnitArchivedQuantity(order, products, productId, unitId);
      if (total > 0) {
        dataPoints.push({
          x: new Date(order.dateClosed),
          y: total,
        });
      }
    }
  });

  if (includeOpen && orders.current) {
    const total = getProductUnitOpenQuantity(orders.current, products, productId, unitId);
    if (total > 0) {
      dataPoints.push({
        x: new Date(),
        y: total,
      });
    }
  }

  return dataPoints;
}

export function getProductUnitsWithSales(orders, products) {
  const puWithSales = new Set();
  
  const processOrder = (order) => {
    for (const clientId in order.byClient) {
      const clientData = order.byClient[clientId];
      Object.keys(clientData).forEach(productId => {
        if (productId !== 'adjustment') {
          const productOrder = clientData[productId];
          const prod = products.byId[productId];
          if (prod && productOrder) {
            const uId = productOrder.unitId >= prod.price.length ? prod.price.length - 1 : (productOrder.unitId || 0);
            puWithSales.add(`${productId}-${uId}`);
          }
        }
      });
    }
  };

  orders.archive.forEach(processOrder);
  if (orders.current) {
    processOrder(orders.current);
  }
  
  return puWithSales;
}

export function getTopProductUnitsByMode(orders, products, mode, ytdYear, n, includeOpen = true) {
  const pointsByPU = {};
  const allPUIds = getProductUnitsWithSales(orders, products);

  allPUIds.forEach(puId => {
    const [productId, unitIdStr] = puId.split('-');
    const unitId = parseInt(unitIdStr, 10);
    pointsByPU[puId] = convertOrdersToProductUnitDataPoints(orders, products, productId, unitId, includeOpen);
  });

  let globalYears = null;
  if (mode === ChartMode.YEARLY) {
    const yearsSet = new Set();
    allPUIds.forEach(puId => {
      pointsByPU[puId].forEach(pt => yearsSet.add(new Date(pt.x).getFullYear()));
    });
    globalYears = Array.from(yearsSet).map(Number).sort((a,b) => a-b);
  }

  const totals = Array.from(allPUIds).map(puId => {
    const daily = aggregateByDay(pointsByPU[puId]);
    const chartData = processData(daily, mode, undefined, ytdYear, globalYears);

    let relevantTotal = 0;
    if (chartData.values && chartData.values.length > 0) {
      if (mode === ChartMode.YEARLY) {
        const targetYear = globalYears.length > 0
          ? Math.max(globalYears[globalYears.length - 1], new Date().getFullYear())
          : new Date().getFullYear();
        const yearIndex = chartData.labels.indexOf(String(targetYear));
        if (yearIndex >= 0) relevantTotal = chartData.values[yearIndex];
      } else if (mode === ChartMode.YTD) {
        relevantTotal = chartData.values[chartData.values.length - 1];
      } else if (mode === ChartMode.YTD_ACCUMULATED || mode === ChartMode.WEEKLY) {
        relevantTotal = chartData.values.reduce((sum, v) => sum + v, 0);
      }
    }

    return { id: puId, total: relevantTotal };
  });

  totals.sort((a, b) => b.total - a.total);
  return totals.slice(0, n).map(p => p.id);
}

class ProductVolumesChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedProductUnitIds: [],
      currentMode: ChartMode.YTD,
      currentYtdYear: null,
      includeOpen: true,
    };
  }

  getProductUnitName = (puId) => {
    if (!puId) return '';
    const [productId, unitIdStr] = puId.split('-');
    const unitId = parseInt(unitIdStr, 10);
    const prod = this.props.products.byId[productId];
    if (!prod) return '';
    const unitName = prod.price[unitId] ? prod.price[unitId].unit : '';
    return `${prod.name} (${unitName})`;
  }

  renderTag = (puId) => {
    return this.getProductUnitName(puId);
  }

  handleTagRemove = (_tagLabel, index) => {
    this.setState(s => ({
      selectedProductUnitIds: s.selectedProductUnitIds.filter((_, i) => i !== index),
    }));
  }

  handleProductUnitSelect = (puId) => {
    this.setState(s => {
      const selected = s.selectedProductUnitIds;
      if (selected.includes(puId)) {
        return { selectedProductUnitIds: selected.filter(selId => selId !== puId) };
      } else {
        return { selectedProductUnitIds: [...selected, puId] };
      }
    });
  }

  filterProductUnit = (query, puId) => {
    const name = this.getProductUnitName(puId);
    return name.toLowerCase().includes(query.toLowerCase());
  }

  renderProductUnitItem = (puId, options) => {
    if (!options.modifiers.matchesPredicate) return null;
    const isSelected = this.state.selectedProductUnitIds.includes(puId);

    return (
      <MenuItem
        key={puId}
        active={options.modifiers.active}
        icon={isSelected ? "tick" : "blank"}
        text={this.getProductUnitName(puId)}
        onClick={options.handleClick}
        shouldDismissPopover={false}
      />
    );
  }

  handleChartModeChange = (mode, ytdYear) => {
    this.setState({ currentMode: mode, currentYtdYear: ytdYear });
  }

  handleToggleOpen = (e) => {
    this.setState({ includeOpen: e.target.checked });
  }

  render() {
    const { orders, products } = this.props;
    const { selectedProductUnitIds, currentMode, currentYtdYear, includeOpen } = this.state;

    const puWithSales = getProductUnitsWithSales(orders, products);
    const availableProductUnits = Array.from(puWithSales)
      .sort((a, b) => this.getProductUnitName(a).localeCompare(this.getProductUnitName(b)));

    let activePUIds = selectedProductUnitIds;
    if (activePUIds.length === 0) {
      activePUIds = getTopProductUnitsByMode(orders, products, currentMode, currentYtdYear, 5, includeOpen);
    }

    const datasets = activePUIds.map((puId, index) => {
      const [productId, unitIdStr] = puId.split('-');
      const unitId = parseInt(unitIdStr, 10);
      return {
        label: this.getProductUnitName(puId),
        dataPoints: convertOrdersToProductUnitDataPoints(orders, products, productId, unitId, includeOpen),
        color: CHART_COLORS[index % CHART_COLORS.length],
      };
    });

    var info = (
      <div>
        É possível selecionar quantos produtos forem necessários.<br />
        Se nenhum produto for selecionado, serão exibidos os 5 produtos com maior volume de vendas.<br />
      </div>
    );

    return (
      <div>
        <h3 style={{ textAlign: 'center' }} className="pt-heading">Volume por Produto</h3>
        <div style={{ marginBottom: '20px' }}>
          <div className="pt-label" style={{ marginBottom: '8px' }}>
            <Tooltip content={info} position={Position.RIGHT}>
              <Button rightIcon="info-sign" small minimal>
                <b>Selecione um ou mais Produtos</b>
              </Button>
            </Tooltip>
          </div>
          <MultiSelect
            items={availableProductUnits}
            itemPredicate={this.filterProductUnit}
            itemRenderer={this.renderProductUnitItem}
            onItemSelect={this.handleProductUnitSelect}
            tagRenderer={this.renderTag}
            tagInputProps={{
              onRemove: this.handleTagRemove,
              inputProps: {
                placeholder: 'Pesquisar...',
              },
            }}
            selectedItems={selectedProductUnitIds}
            popoverProps={{ minimal: true }}
            resetOnSelect={true}
          />
        </div>
        <Checkbox 
          label="Incluir pedidos em aberto" 
          checked={includeOpen} 
          onChange={this.handleToggleOpen} 
          style={{ marginBottom: '10px' }}
        />
        <TimeSeriesChart
          datasets={datasets}
          valueType={ValueType.QUANTITY}
          onModeChange={this.handleChartModeChange}
          fillArea={false}
        />
      </div>
    );
  }
}

ProductVolumesChart.propTypes = {
  orders: PropTypes.object.isRequired,
  products: PropTypes.object.isRequired,
};

export default ProductVolumesChart;



// WEBPACK FOOTER //
// ./src/components/ChartView/ProductVolumesChart.js